import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import {
  ChevronRight,
  Database,
  MessageCircle,
  Navigation,
  Search,
  Sparkles,
  Table,
  User,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import type { ToolCallEvent } from '../stores/chatStore';
import styles from './ToolCallDisplay.module.css';

/** Human-readable descriptions for tool calls */
const TOOL_DESCRIPTIONS: Record<string, (args: Record<string, unknown>) => string> = {
  navigate_to_tab: (args) => {
    const labels: Record<string, string> = {
      home: 'Home',
      'collector-record': 'Collector',
      evaluator: 'Evaluator',
      episodes: 'Episodes',
      datasets: 'Datasets',
      'ml-studio': 'ML',
      devservers: 'Devservers',
      settings: 'Settings',
    };
    return `Navigate to ${labels[args.tab_id as string] ?? args.tab_id}`;
  },
  set_episode_filters: (args) => {
    const parts: string[] = [];
    if (args.search) parts.push(`search: "${args.search}"`);
    if (args.source) parts.push(`source: ${args.source}`);
    if (args.collector_id) parts.push(`collector set`);
    if (args.date_from || args.date_to) {
      const from = (args.date_from as string) ?? '...';
      const to = (args.date_to as string) ?? '...';
      parts.push(`dates: ${from} to ${to}`);
    }
    if (args.rating !== undefined) parts.push(`rating: ${args.rating}`);
    if (args.reviewed !== undefined) parts.push(`reviewed: ${args.reviewed}`);
    if (args.view_mode) parts.push(`view: ${args.view_mode}`);
    return parts.length > 0 ? `Set filters: ${parts.join(', ')}` : 'Set episode filters';
  },
  clear_episode_filters: () => 'Clear all episode filters',
  set_collection_task: (args) => `Set task: "${args.task}"`,
  set_collection_presets: (args) => {
    const parts: string[] = [];
    if (args.task) parts.push(`task: "${args.task}"`);
    if (args.fps) parts.push(`${args.fps} fps`);
    if (args.hardware_version) parts.push(`hw: ${args.hardware_version}`);
    return parts.length > 0 ? `Set presets: ${parts.join(', ')}` : 'Set collection presets';
  },
  query_supabase: (args) => {
    const table = (args.table as string) ?? 'unknown';
    const parts: string[] = [`table: ${table}`];
    if (args.count) parts.push('count');
    if (args.filters && Object.keys(args.filters as object).length > 0) {
      parts.push(`${Object.keys(args.filters as object).length} filter(s)`);
    }
    if (args.limit !== undefined) parts.push(`limit: ${args.limit}`);
    return `Query: ${parts.join(', ')}`;
  },
  lookup_user: (args) => {
    const userId = args.user_id as string | undefined;
    if (userId) {
      const short = userId.length > 12 ? `${userId.slice(0, 8)}…` : userId;
      return `Resolve user ${short}`;
    }
    const search = args.search as string | undefined;
    return search ? `Search users: "${search}"` : 'Lookup user';
  },
  fetch_dagster: (args) => {
    const action = (args.action as string)?.replace(/_/g, ' ') ?? 'data';
    return `Fetch ${action} → "${args.buffer}"`;
  },
  check_gpu_availability: (args) => `Check GPU availability → "${args.buffer}"`,
  run_buffer_command: (args) => `Run: ${args.command}`,
  fetch_repo: (args) => {
    const action = (args.action as string)?.replace(/_/g, ' ') ?? 'data';
    const path = args.path ? ` (${args.path})` : '';
    return `Repo ${action}${path} → "${args.buffer}"`;
  },
  navigate_to_dataset: () => 'Open dataset',
  navigate_to_experiment: () => 'Open workflow run',
  navigate_to_workflow_run: () => 'Open workflow run',
};

function getDescription(name: string, args: Record<string, unknown>): string {
  const fn = TOOL_DESCRIPTIONS[name];
  if (fn) return fn(args);
  return name.replace(/_/g, ' ');
}

const TOOL_LABELS: Record<string, string> = {
  describe_dashboard_schema: 'Inspecting dashboard schema',
  run_dashboard_explore_sql: 'Probing dashboard data',
  propose_widget: 'Streaming widget',
  propose_dashboard: 'Updated dashboard artifact',
  query_dashboard_widget: 'Reading dashboard widget',
  query_supabase: 'Querying data',
  lookup_user: 'Resolving user',
  navigate_to_tab: 'Navigating',
  set_episode_filters: 'Setting filters',
  clear_episode_filters: 'Clearing filters',
  fetch_dagster: 'Fetching Dagster data',
  check_gpu_availability: 'Checking GPU availability',
  run_buffer_command: 'Running buffer command',
  fetch_repo: 'Reading repository',
};

function getLabel(name: string): string {
  return TOOL_LABELS[name] ?? name.replace(/_/g, ' ');
}

function iconForTool(name: string): LucideIcon {
  if (name.includes('dashboard') || name === 'propose_widget') return Sparkles;
  if (name.includes('schema')) return Table;
  if (name.includes('query') || name.includes('sql')) return Database;
  if (name.includes('lookup_user')) return User;
  if (name.includes('navigate')) return Navigation;
  if (name.includes('filter') || name.includes('search')) return Search;
  if (name.includes('ask')) return MessageCircle;
  return Wrench;
}

interface ToolCallDisplayProps {
  toolCall: ToolCallEvent;
  /** When true, render a minimal single-line layout (icon + description only). */
  compact?: boolean;
  onConfirm?: (toolCallId: string) => void;
  onCancel?: (toolCallId: string) => void;
}

function tryFormatJson(text: string): string {
  const trimmed = text.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return text;
  try {
    return JSON.stringify(JSON.parse(trimmed), null, 2);
  } catch {
    return text;
  }
}

export function ToolCallDisplay({
  toolCall,
  compact = false,
  onConfirm,
  onCancel,
}: ToolCallDisplayProps) {
  const [expanded, setExpanded] = useState(false);
  const { name, args, status, result, resultTruncated, resultLength } = toolCall;
  const confirmRef = useRef<HTMLButtonElement | null>(null);
  const description = getDescription(name, args);
  const label = getLabel(name);
  const Icon = iconForTool(name);
  const hasArgs = Object.keys(args).length > 0;
  const hasResult = typeof result === 'string' && result.length > 0;
  const canExpand = hasArgs || hasResult;
  const needsConfirmation = status === 'awaiting_confirmation' && toolCall.confirmation;

  useEffect(() => {
    if (needsConfirmation) confirmRef.current?.focus();
  }, [needsConfirmation]);

  // The row opens and closes the details. Enter or Space on the focused
  // row does the same as a click.
  const handleRowKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    setExpanded(!expanded);
  };

  const statusClass =
    status === 'executed' || status === 'rendered'
      ? styles.statusExecuted
      : status === 'cancelled'
        ? styles.statusCancelled
      : status === 'error'
        ? styles.statusError
        : styles.statusPending;

  if (compact) {
    return (
      <div className={`${styles.compactToolCall} ${statusClass}`}>
        <span className={styles.statusDot} />
        <span className={styles.compactDescription}>{description}</span>
      </div>
    );
  }

  return (
    <div
      className={`${styles.toolCall} ${statusClass} ${canExpand ? styles.clickable : ''}`}
    >
      <div
        className={styles.toolCallRow}
        onClick={canExpand ? () => setExpanded(!expanded) : undefined}
        onKeyDown={canExpand ? handleRowKeyDown : undefined}
        role={canExpand ? 'button' : undefined}
        tabIndex={canExpand ? 0 : undefined}
        aria-expanded={canExpand ? expanded : undefined}
      >
        <span className={styles.toolCallIcon}>
          <Icon size={14} strokeWidth={1.75} />
        </span>
        <div className={styles.toolCallContent}>
          <span className={styles.toolCallName}>{label}</span>
          <span className={styles.toolCallDescription}>{description}</span>
        </div>
        {status === 'pending' ? (
          <span className={`${styles.toolCallPendingTag} ${styles.pillRunning}`}>Running…</span>
        ) : null}
        {status === 'rendered' ? (
          <span className={`${styles.toolCallPendingTag} ${styles.pillDone}`}>Shown</span>
        ) : null}
        {status === 'awaiting_confirmation' ? (
          <span className={`${styles.toolCallPendingTag} ${styles.pillHelp}`}>Needs approval</span>
        ) : null}
        {status === 'error' ? (
          <span className={styles.toolCallErrorTag}>Error</span>
        ) : null}
        {status === 'cancelled' ? (
          <span className={`${styles.toolCallPendingTag} ${styles.pillStopped}`}>Cancelled</span>
        ) : null}
        {canExpand && (
          <ChevronRight
            className={`${styles.chevron} ${expanded ? styles.chevronExpanded : ''}`}
          />
        )}
      </div>
      {expanded && canExpand && (
        <div className={styles.expandedBody}>
          {hasResult && (
            <div className={styles.expandedSection}>
              <div className={styles.expandedLabel}>
                <span>Result</span>
                {resultTruncated && (
                  <span className={styles.truncatedNote}>
                    Truncated{typeof resultLength === 'number' ? ` · ${resultLength.toLocaleString()} chars total` : ''}
                  </span>
                )}
              </div>
              <pre className={styles.toolCallArgs}>
                {tryFormatJson(result!)}
              </pre>
            </div>
          )}
          {hasArgs && (
            <div className={styles.expandedSection}>
              <div className={styles.expandedLabel}>
                <span>Arguments</span>
              </div>
              <pre className={styles.toolCallArgs}>
                {JSON.stringify(args, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
      {needsConfirmation ? (
        <div className={styles.confirmation}>
          <div className={styles.confirmationText}>
            {toolCall.confirmation?.message}
          </div>
          <div className={styles.confirmationActions}>
            <button
              ref={confirmRef}
              type="button"
              className={styles.confirmButton}
              onClick={() => onConfirm?.(toolCall.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                  event.preventDefault();
                  onConfirm?.(toolCall.id);
                  return;
                }
                if (event.key === 'Escape') {
                  event.preventDefault();
                  onCancel?.(toolCall.id);
                }
              }}
            >
              {toolCall.confirmation?.confirmLabel ?? 'Confirm'}
            </button>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={() => onCancel?.(toolCall.id)}
            >
              {toolCall.confirmation?.cancelLabel ?? 'Cancel'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
